import Button from '@rio-cloud/rio-uikit/Button';
import EmptyState from '@rio-cloud/rio-uikit/EmptyState';
import SimpleTooltip from '@rio-cloud/rio-uikit/SimpleTooltip';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { type ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useGetVehicleAvailabilityQuery } from '#api-bridge/vehicleAvailabilityApi.ts';
import { getVehicleDisplayName } from '#domain/Vehicle.ts';
import type { VehicleAvailability, VehicleAvailabilityTour } from '#domain/VehicleAvailability.ts';

import type { TourStop } from './TourStop.ts';
import {
    getConflictingTours,
    getProposedTourInterval,
    getTimelineBarPosition,
    getTimelineGuides,
    getTimelineRange,
    getTimelineTourLayout,
    intervalsOverlap,
    type TimelineInterval,
    type TimelineRange,
} from './vehicleAvailability.utils.ts';

type VehicleAvailabilityTimelineProps = {
    stops: TourStop[];
    selectedVehicleId: string;
    onSelectVehicle: (vehicleId: string) => void;
    excludedTourId?: string;
    disabled?: boolean;
    skip?: boolean;
    capacityControls?: ReactNode;
};

const VEHICLE_COLUMN_WIDTH = 150;
const MINIMUM_DAY_WIDTH = 120;
const BAR_HEIGHT = 35;
const BAR_GAP = 15;
const VEHICLE_ROW_HEIGHT = BAR_HEIGHT + BAR_GAP * 2;

const TimelineGuides = ({ range }: { range: TimelineRange }) => (
    <div className='position-absolute top-0 bottom-0 left-0 right-0 pointer-events-none' aria-hidden='true'>
        {getTimelineGuides(range).map(({ time, hour, left }) => (
            <span
                key={time.toMillis()}
                className={`position-absolute top-0 bottom-0 border border-left-only ${hour === 0 ? 'border-color-light' : 'border-color-lighter'}`}
                style={{ left }}
                data-testid='timeline-guide'
                data-hour={hour}
            />
        ))}
    </div>
);

const toIso = (dateTime: TimelineRange['start']) => {
    const iso = dateTime.toUTC().toISO();
    if (!iso) {
        throw new Error('Cannot create a vehicle availability query from an invalid date');
    }
    return iso;
};

const formatRange = (locale: string, tour: VehicleAvailabilityTour) => {
    const start = new Date(tour.start);
    const end = new Date(tour.end);
    const dateFormatter = new Intl.DateTimeFormat(locale, { dateStyle: 'short' });
    const timeFormatter = new Intl.DateTimeFormat(locale, { timeStyle: 'short' });
    return `${dateFormatter.format(start)}, ${timeFormatter.format(start)} – ${dateFormatter.format(end)}, ${timeFormatter.format(end)}`;
};

const TimelineBar = ({
    interval,
    range,
    label,
    title,
    className,
    top,
    zIndex = 0,
}: {
    interval: TimelineInterval;
    range: TimelineRange;
    label: string;
    title: string;
    className: string;
    top: number;
    zIndex?: number;
}) => (
    <span
        className={`position-absolute display-flex align-items-center rounded padding-x-10 text-size-14 text-medium text-overflow-ellipsis overflow-hidden ${className}`}
        style={{
            ...getTimelineBarPosition(interval, range),
            top,
            zIndex,
            height: BAR_HEIGHT,
            minWidth: 3,
            whiteSpace: 'nowrap',
        }}
        title={title}
    >
        {label}
    </span>
);

const VehicleLane = ({
    vehicle,
    range,
    proposedTour,
    selected,
    disabled,
    conflictingTourIds,
    proposedLabel,
    conflictWarning,
    onSelect,
}: {
    vehicle: VehicleAvailability;
    range: TimelineRange;
    proposedTour?: TimelineInterval;
    selected: boolean;
    disabled: boolean;
    conflictingTourIds: Set<string>;
    proposedLabel: string;
    conflictWarning?: string;
    onSelect: () => void;
}) => {
    const suitabilityClass = conflictingTourIds.size > 0 ? 'alert-warning' : 'bg-white';
    const { entries } = useMemo(() => getTimelineTourLayout(vehicle.tours), [vehicle.tours]);
    const overlapsProposed =
        selected && proposedTour && entries.some(entry => intervalsOverlap(entry.interval, proposedTour));

    return (
        <button
            type='button'
            className={`alert display-flex width-100pct margin-0 padding-0 rounded-none border-top border-top-only border-width-1 border-color-light text-left ${suitabilityClass} ${selected ? 'bg-highlight-lightest' : ''} ${disabled ? '' : 'cursor-pointer'}`}
            aria-pressed={selected}
            aria-label={getVehicleDisplayName(vehicle)}
            aria-describedby={conflictWarning ? `vehicle-conflict-${vehicle.vehicleId}` : undefined}
            disabled={disabled}
            onClick={onSelect}
        >
            <div
                className={`alert ${suitabilityClass} position-sticky flex-shrink-0 display-flex align-items-center margin-0 padding-15 rounded-none border border-right-only border-width-1 border-color-light ${selected ? 'bg-highlight-lightest text-color-primary' : ''}`}
                style={{ width: VEHICLE_COLUMN_WIDTH, left: 0, zIndex: 3 }}
            >
                <span
                    className='text-overflow-ellipsis overflow-hidden text-nowrap'
                    title={[getVehicleDisplayName(vehicle), vehicle.searchName].filter(Boolean).join(' · ')}
                >
                    <strong className='display-block text-overflow-ellipsis overflow-hidden'>
                        {getVehicleDisplayName(vehicle)}
                    </strong>
                </span>
                {conflictWarning && (
                    <SimpleTooltip content={conflictWarning}>
                        <span
                            id={`vehicle-conflict-${vehicle.vehicleId}`}
                            role='img'
                            aria-label={conflictWarning}
                            className='rioglyph rioglyph-warning-sign text-color-warning flex-shrink-0 margin-left-5'
                            data-testid='vehicle-conflict-warning'
                        />
                    </SimpleTooltip>
                )}
            </div>
            <div className='position-relative flex-1-1-0 overflow-hidden' style={{ height: VEHICLE_ROW_HEIGHT }}>
                <TimelineGuides range={range} />
                {entries.map(({ tour, interval }) => (
                    <TimelineBar
                        key={tour.id}
                        interval={interval}
                        range={range}
                        label={tour.name}
                        title={tour.name}
                        top={BAR_GAP}
                        zIndex={conflictingTourIds.has(tour.id) ? 2 : 0}
                        className={`${
                            conflictingTourIds.has(tour.id)
                                ? 'bg-warning text-color-dark'
                                : 'bg-lighter text-color-dark'
                        } ${conflictingTourIds.has(tour.id) ? 'opacity-80' : entries.some(other => other.tour.id !== tour.id && intervalsOverlap(other.interval, interval)) ? 'opacity-50' : ''}`}
                    />
                ))}
                {selected && proposedTour && (
                    <TimelineBar
                        interval={proposedTour}
                        range={range}
                        label={proposedLabel}
                        title={proposedLabel}
                        top={BAR_GAP}
                        zIndex={1}
                        className={`bg-primary text-color-white ${overlapsProposed ? 'opacity-50' : ''}`}
                    />
                )}
            </div>
        </button>
    );
};

export const VehicleAvailabilityTimeline = ({
    stops,
    selectedVehicleId,
    onSelectVehicle,
    excludedTourId,
    disabled = false,
    skip = false,
    capacityControls,
}: VehicleAvailabilityTimelineProps) => {
    const intl = useIntl();
    const proposedTour = useMemo(() => getProposedTourInterval(stops), [stops]);
    const range = useMemo(() => getTimelineRange(proposedTour), [proposedTour]);
    const query = useMemo(() => ({ from: toIso(range.start), to: toIso(range.end) }), [range]);
    const {
        currentData: data = [],
        isFetching,
        isError,
        refetch,
    } = useGetVehicleAvailabilityQuery(query, {
        skip,
        refetchOnMountOrArgChange: true,
    });
    const vehicles = useMemo(
        () => [...data].sort((left, right) => getVehicleDisplayName(left).localeCompare(getVehicleDisplayName(right))),
        [data]
    );
    const selectedVehicle = vehicles.find(vehicle => vehicle.vehicleId === selectedVehicleId);
    const conflicts = getConflictingTours(selectedVehicle?.tours ?? [], proposedTour, excludedTourId);
    const title = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.title',
        defaultMessage: 'Vehicle availability',
    });
    const proposedLabel = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.proposed-tour',
        defaultMessage: 'Proposed tour',
    });
    const conflictMessage = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.conflict',
        defaultMessage: 'The selected vehicle has overlapping tours.',
    });
    const conflictDetails = conflicts.map(tour => `${tour.name}: ${formatRange(intl.locale, tour)}`).join('; ');

    return (
        <section
            className='display-flex flex-column flex-1-1 height-100pct gap-10 min-height-0 min-width-0'
            aria-label={title}
            aria-busy={isFetching}
        >
            <div className='display-flex flex-wrap justify-content-between align-items-center gap-10 flex-shrink-0'>
                <h4 className='margin-0'>{title}</h4>
                {capacityControls}
            </div>
            {isFetching ? (
                <div className='display-grid place-items-center min-height-100'>
                    <Spinner />
                </div>
            ) : isError ? (
                <EmptyState
                    condensed
                    outerClassName='border-none margin-0'
                    headline={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.error',
                        defaultMessage: 'Vehicle availability could not be loaded.',
                    })}
                    buttons={[
                        {
                            bsStyle: Button.PRIMARY,
                            onClick: () => refetch(),
                            text: intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.retry',
                                defaultMessage: 'Retry',
                            }),
                        },
                    ]}
                />
            ) : vehicles.length === 0 ? (
                <EmptyState
                    condensed
                    outerClassName='border-none margin-0'
                    headline={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.vehicle-availability.empty',
                        defaultMessage: 'No vehicles are available.',
                    })}
                />
            ) : (
                <div className='border rounded bg-white shadow-subtle overflow-auto flex-1-1 min-height-0'>
                    <div style={{ minWidth: VEHICLE_COLUMN_WIDTH + range.days.length * MINIMUM_DAY_WIDTH }}>
                        <div className='display-flex bg-lightest position-sticky top-0' style={{ zIndex: 4 }}>
                            <div
                                className='position-sticky flex-shrink-0 padding-15 text-medium border border-right-only bg-lightest'
                                style={{ width: VEHICLE_COLUMN_WIDTH, left: 0, zIndex: 5 }}
                            >
                                {intl.formatMessage({
                                    id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.tourTruck',
                                    defaultMessage: 'Vehicle',
                                })}
                            </div>
                            {range.days.map(day => (
                                <div
                                    key={day.toISODate()}
                                    className='flex-1-1-0 padding-top-10 border border-right-only text-center text-nowrap'
                                    style={{ minWidth: MINIMUM_DAY_WIDTH }}
                                >
                                    <div className='text-medium padding-x-5 margin-bottom-10'>
                                        {day
                                            .setLocale(intl.locale)
                                            .toLocaleString({ weekday: 'short', day: '2-digit', month: '2-digit' })}
                                    </div>
                                    <div
                                        className='display-grid grid-cols-4 text-size-12 text-color-dark border border-top-only'
                                        aria-hidden='true'
                                    >
                                        {['00', '06', '12', '18'].map(hour => (
                                            <span key={hour} className='padding-y-5 border border-right-only'>
                                                {hour}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {vehicles.map(vehicle => {
                            const vehicleConflicts = getConflictingTours(vehicle.tours, proposedTour, excludedTourId);
                            return (
                                <VehicleLane
                                    key={vehicle.vehicleId}
                                    vehicle={vehicle}
                                    range={range}
                                    proposedTour={proposedTour}
                                    selected={vehicle.vehicleId === selectedVehicleId}
                                    disabled={disabled}
                                    conflictingTourIds={new Set(vehicleConflicts.map(tour => tour.id))}
                                    proposedLabel={proposedLabel}
                                    conflictWarning={
                                        vehicle.vehicleId === selectedVehicleId && conflicts.length > 0
                                            ? `${conflictMessage} ${conflictDetails}`
                                            : undefined
                                    }
                                    onSelect={() => onSelectVehicle(vehicle.vehicleId)}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </section>
    );
};
